import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, FileSpreadsheet } from "lucide-react";
import { BACKEND, api, getToken } from "@/lib/api";
import { fmtDay, fmtEuro } from "@/lib/format";
import { Btn, PageHeader, Pill, Spinner } from "@/components/rp";
import { Section } from "@/components/commesse";
import { invoiceText } from "@/components/admin/invoice";

const WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const href = (e) => `/admin/amministrazione/${e.order_id}`;

const Chipline = ({ e }) => (
  <Link to={href(e)} title={`${e.code} · ${e.label} · ${e.counterpart}`} data-testid={`cal-entry-${e.scope}-${e.key}-${e.order_id}`}
    className={`block truncate rounded-md px-1.5 py-0.5 text-[11px] ${e.direction === "entrata" ? "bg-[#198754]/10 text-[#198754]" : "bg-[#070707]/10 text-black"} ${e.done ? "opacity-60 line-through" : ""}`}>
    {e.direction === "entrata" ? "+" : "−"}{e.amount != null ? fmtEuro(e.amount) : "?"} {e.counterpart}
  </Link>
);

const Row = ({ e, red }) => (
  <Link to={href(e)} className={`flex flex-wrap items-center gap-3 p-3 rounded-2xl border ${red ? "border-[#DC3545]/30 bg-[#DC3545]/5" : "border-[#EBEBEB] bg-white"} hover:border-[#C8BDAB]`} data-testid={`sched-row-${e.scope}-${e.key}-${e.order_id}`}>
    <span className="text-xs w-20 text-[#757575]">{e.day ? fmtDay(e.day) : "—"}</span>
    <span className="flex-1 min-w-[180px] text-sm"><b>{e.label}</b> · {e.counterpart} <span className="text-[#757575]">· {e.code} {e.title}</span>{invoiceText(e) && <span className="block text-xs text-[#198754]" data-testid={`sched-invoice-${e.scope}-${e.key}-${e.order_id}`}>{invoiceText(e)}</span>}</span>
    <span className={`text-sm font-semibold ${e.direction === "entrata" ? "text-[#198754]" : ""}`}>{e.direction === "entrata" ? "+" : "−"}{e.amount != null ? fmtEuro(e.amount) : "importo da definire"}</span>
    <Pill tone={e.done ? "success" : red ? "error" : "warning"}>{e.done ? (e.direction === "entrata" ? "Fatturata" : "Pagato") : red ? "Scaduta" : "Da fare"}</Pill>
  </Link>
);

const Totals = ({ t }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="sched-totals">
    {[["Fatturato ai clienti", t.entrate_fatte, "in"], ["Da fatturare", t.entrate_da_fare, "in"], ["Pagato ai fornitori", t.uscite_fatte, "out"], ["Da pagare", t.uscite_da_fare, "out"], ["Differenza del mese", t.saldo, "bal"]].map(([l, v, k], i) => (
      <div key={l} className={`rp-card p-4 ${k === "bal" ? "bg-[#070707] text-white border-[#070707]" : ""}`} data-testid={`sched-total-${i}`}>
        <p className={`text-xs ${k === "bal" ? "text-white/70" : "text-[#757575]"}`}>{l}</p>
        <p className={`text-2xl font-semibold mt-2 ${k === "in" ? "text-[#198754]" : ""}`}>{fmtEuro(v)}</p>
      </div>
    ))}
  </div>
);

const Calendar = ({ month, entries }) => {
  const [y, m] = month.split("-").map(Number);
  const days = new Date(y, m, 0).getDate();
  const offset = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const todayIso = new Date().toLocaleDateString("en-CA");
  return (
    <div className="hidden md:grid grid-cols-7 gap-1 mb-6" data-testid="sched-calendar">
      {WEEK.map((w) => <p key={w} className="rp-label text-center py-1">{w}</p>)}
      {cells.map((d, i) => {
        const iso = d && `${month}-${String(d).padStart(2, "0")}`;
        const list = d ? entries.filter((e) => e.day === iso) : [];
        return (
          <div key={i} className={`min-h-[96px] rounded-xl border p-1.5 space-y-1 ${!d ? "border-transparent" : iso === todayIso ? "border-black" : "border-[#EBEBEB] bg-white"}`} data-testid={d ? `cal-day-${d}` : undefined}>
            {d && <p className="text-xs text-[#757575]">{d}</p>}
            {list.slice(0, 4).map((e) => <Chipline key={`${e.order_id}-${e.scope}-${e.key}`} e={e} />)}
            {list.length > 4 && <p className="text-[11px] text-[#757575]">+{list.length - 4} altre</p>}
          </div>
        );
      })}
    </div>
  );
};

export default function AdminScadenzario() {
  const [month, setMonth] = useState(ym(new Date()));
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/amministrazione/scadenzario", { params: { month } }).then((r) => setData(r.data)); }, [month]);
  const shift = (n) => { const [y, m] = month.split("-").map(Number); setMonth(ym(new Date(y, m - 1 + n, 1))); };
  const label = new Date(`${month}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return (
    <div data-testid="admin-scadenzario-page">
      <Link to="/admin/amministrazione" className="inline-flex items-center gap-2 text-sm text-[#757575] hover:text-black mb-4" data-testid="back-to-pratiche"><ArrowLeft className="h-4 w-4" /> Amministrazione</Link>
      <PageHeader label="Scadenzario pagamenti" title={<span className="capitalize" data-testid="sched-month-label">{label}</span>}>
        <Btn variant="outline" onClick={() => shift(-1)} data-testid="sched-prev"><ChevronLeft className="h-4 w-4" /></Btn>
        <Btn variant="outline" onClick={() => setMonth(ym(new Date()))} data-testid="sched-today">Oggi</Btn>
        <Btn variant="outline" onClick={() => shift(1)} data-testid="sched-next"><ChevronRight className="h-4 w-4" /></Btn>
        <a href={`${BACKEND}/api/amministrazione/scadenzario/export.xlsx?${new URLSearchParams({ month, token: getToken() })}`} data-testid="sched-export-button"
          className="rp-btn bg-[#070707] text-white hover:bg-black/80"><FileSpreadsheet className="h-4 w-4" /> Esporta Excel{data ? ` (${data.entries.length})` : ""}</a>
      </PageHeader>
      {!data ? <Spinner /> : (
        <>
          <Totals t={data.totals} />
          {data.overdue.length > 0 && (
            <Section label={`Arretrati (${data.overdue.length})`} className="border-[#DC3545]/40 mb-6" testid="sched-overdue">
              <div className="space-y-2">{data.overdue.map((e) => <Row key={`${e.order_id}-${e.scope}-${e.key}`} e={e} red />)}</div>
            </Section>
          )}
          <Calendar month={month} entries={data.entries} />
          <Section label={`Voci del mese (${data.entries.length})`} testid="sched-list">
            {!data.entries.length && <p className="text-sm text-[#757575]">Nessuna fattura o pagamento in questo mese.</p>}
            <div className="space-y-2">{data.entries.map((e) => <Row key={`${e.order_id}-${e.scope}-${e.key}`} e={e} />)}</div>
          </Section>
          {data.undated.length > 0 && (
            <Section label={`Senza data (${data.undated.length})`} className="mt-6" testid="sched-undated">
              <p className="text-xs text-[#757575] mb-3">Fatture e pagamenti senza scadenza: impostala nella pratica per vederli nel calendario.</p>
              <div className="space-y-2">{data.undated.map((e) => <Row key={`${e.order_id}-${e.scope}-${e.key}`} e={e} />)}</div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
